import { NgClass } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
} from "@angular/core";
import { ButtonDirective } from "primeng/button";
import { TooltipModule } from "primeng/tooltip";

import { NotifyService } from "app/notify/notify.service";
import {
  AttachmentDeleteReason,
  QuoteAttachmentRepository,
} from "app/quote/quote-attachment.repository";
import { QuoteEmailAttachment } from "app/quote/models/email-attachment.model";

@Component({
  selector: "app-quote-email-attachments",
  templateUrl: "./quote-email-attachments.component.html",
  styleUrls: ["./quote-email-attachments.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonDirective, TooltipModule, NgClass],
})
export class QuoteEmailAttachmentsComponent {
  public readonly quoteId = input.required<number>();
  public readonly attachments = input.required<QuoteEmailAttachment[]>();
  /** Deleting is gated on the same permission as editing the quote it belongs to. */
  public readonly canDelete = input<boolean>(false);

  private readonly repository = inject(QuoteAttachmentRepository);
  private readonly notify = inject(NotifyService);

  private readonly expanded = signal<number[]>([]);
  /** The row whose delete is being confirmed, and the reason picked for it. */
  public readonly confirming = signal<number>(null);
  public readonly chosenReason = signal<string>(null);
  public readonly reasons = signal<AttachmentDeleteReason[]>([]);

  /**
   * Every row stays. A deleted file is still part of the record — it shows what was removed and
   * why — so the row changes in place rather than disappearing and coming back looking different
   * after a reload.
   */
  public visible(): QuoteEmailAttachment[] {
    return this.attachments();
  }

  public open(attachment: QuoteEmailAttachment, download: boolean): void {
    this.repository.getUrl(this.quoteId(), attachment, download).subscribe({
      next: (url) => window.open(url, "_blank"),
      error: () =>
        this.notify.alert("This file is no longer available.", "Attachment"),
    });
  }

  /** Opens the reason picker inline rather than in a modal: the choice is the confirmation. */
  public remove(attachment: QuoteEmailAttachment): void {
    this.confirming.set(attachment.getId());
    this.chosenReason.set(null);

    if (this.reasons().length === 0) {
      this.repository
        .getDeleteReasons(this.quoteId())
        .subscribe({ next: (reasons) => this.reasons.set(reasons) });
    }
  }

  public isConfirming(attachment: QuoteEmailAttachment): boolean {
    return this.confirming() === attachment.getId();
  }

  public cancelRemove(): void {
    this.confirming.set(null);
    this.chosenReason.set(null);
  }

  public chooseReason(value: string): void {
    this.chosenReason.set(value);
  }

  public confirmRemove(attachment: QuoteEmailAttachment): void {
    const reason = this.reasons().find(
      (candidate) => candidate.value === this.chosenReason(),
    );
    if (!reason) {
      return;
    }

    this.repository.delete(this.quoteId(), attachment, reason.value).subscribe({
      next: () => {
        attachment.applyDeletion(reason.label, null);
        this.cancelRemove();
      },
      error: () =>
        this.notify.alert("The file could not be deleted.", "Attachment"),
    });
  }

  public sizeLabel(attachment: QuoteEmailAttachment): string {
    const bytes = attachment.getSizeBytes();
    if (bytes < 1024) {
      return `${bytes} B`;
    }

    return bytes < 1024 * 1024
      ? `${Math.round(bytes / 1024)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  public iconClass(attachment: QuoteEmailAttachment): string {
    if (attachment.isArchive()) {
      return "pi-folder";
    }

    const type = attachment.getMimeType() ?? "";
    if (type.startsWith("image/")) {
      return "pi-image";
    }

    return type.includes("sheet") || type.includes("csv")
      ? "pi-table"
      : "pi-file";
  }

  public toggleFields(attachment: QuoteEmailAttachment): void {
    const id = attachment.getId();
    this.expanded.update((ids) =>
      ids.includes(id) ? ids.filter((open) => open !== id) : [...ids, id],
    );
  }

  public isExpanded(attachment: QuoteEmailAttachment): boolean {
    return this.expanded().includes(attachment.getId());
  }

  public isUnattributed(attachment: QuoteEmailAttachment): boolean {
    return attachment.getContributedFields().includes("(not attributed)");
  }
}
